import FA from 'admin/components/atoms/FontAwesome';
import { UsageType, usageTypeLabel } from 'admin/constants/usageType';
import moment from 'moment';
import React from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { Button } from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';

type ApplicationStatus = 'submitted' | 'reviewing' | 'pending' | 'approved' | 'rejected';

// approve / reject は確定操作で元に戻せない。review / hold / unhold は復帰可能。
const isIrreversibleStatus = (status: ApplicationStatus) =>
  status === 'approved' || status === 'rejected';

type ShopFormValues = {
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  usage_type: UsageType | '';
};

type ShopTagSummaryEntry = {
  groupId: string;
  groupName: string;
  itemNames: string[];
  changed: boolean;
};

type FormValues = {
  status: ApplicationStatus;
  business_type: 'individual' | 'corporate';
  private_money_ids: string[];
  name: string;
  furigana: string;
  corporate_number: string;
  representative_name: string;
  representative_furigana: string;
  representative_birth_date: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  capital: string;
  number_of_employees: string;
  referral_source: string;
  bank_name: string;
  bank_code: string;
  bank_branch_name: string;
  bank_branch_code: string;
  bank_account_type: 'saving' | 'current';
  bank_account: string;
  bank_account_holder_name: string;
  shops: ShopFormValues[];
};

type Props = {
  isOpen: boolean;
  formValues: FormValues | null;
  originalValues: FormValues | null;
  organizationCode: string | null;
  privateMoneyNames: string[];
  shopTagSummaries?: ShopTagSummaryEntry[][];
  // 状態を動かさない「変更を保存」操作か。true の場合はステータスに依存せず保存用の文言を出す
  // （target===current のとき status だけでは保存/審査中/保留解除を判別できないため明示的に渡す）。
  isSaveOnly?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const modalStyles = {
  overlay: {
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    padding: 40,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1258,
    maxHeight: '95vh',
    overflow: 'auto',
  },
};

const ModalHeader = styled(Headings.h4)`
  font-size: 20px;
  text-align: center;
  margin: 0;
  width: 100%;
  font-weight: 700;
`;

const ModalContent = styled.div`
  margin: 60px 0;
  display: flex;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
`;

const ColumnSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  border-right: 1px solid #e1e1e1;
  margin: 0 60px;
`;

const ColumnHeader = styled(Headings.h5)`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  margin-bottom: 8px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubSection = styled.div`
  display: flex;
  gap: 24px;
`;

const SectionLabel = styled.label`
  width: 200px;
  color: #9f9f9f;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  display: flex;
  column-gap: 12px;
  row-gap: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
  align-content: flex-start;
`;

const InfoText = styled.p<{ isBold?: boolean }>`
  margin: 0;
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: ${({ isBold = false }) => (isBold ? '700' : '400')};
  line-height: 24px;
  flex: 1;
`;

const RevisedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #f38d22;
  min-width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
  margin-left: 8px;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  margin-top: 16px;
  font-size: 14px;
  font-weight: normal;
`;

const RevisedBadge = () => <RevisedBadgeContainer>修正あり</RevisedBadgeContainer>;

const ShopsSection = styled.div`
  margin-bottom: 8px;
`;

const ShopsScrollArea = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  submitted: '申請受付済',
  reviewing: '審査中',
  pending: '保留中',
  approved: '申請承認済',
  rejected: '却下',
};

const BUSINESS_TYPE_LABELS: Record<'individual' | 'corporate', string> = {
  individual: '個人事業主',
  corporate: '法人',
};

const BANK_ACCOUNT_TYPE_LABELS: Record<'saving' | 'current', string> = {
  saving: '普通',
  current: '当座',
};

const getHeaderMessage = (status: ApplicationStatus) => {
  switch (status) {
    case 'approved':
      return 'この申請を承認します。よろしいですか？';
    case 'rejected':
      return 'この申請を却下します。よろしいですか？';
    case 'reviewing':
      return 'この申請を審査中にします。よろしいですか？';
    case 'pending':
      return 'この申請を保留にします。よろしいですか？';
    case 'submitted':
      return 'この申請の保留を解除します。よろしいですか？';
    default:
      return 'この申請を保存します。よろしいですか？';
  }
};

const getConfirmButtonText = (status: ApplicationStatus) => {
  switch (status) {
    case 'approved':
      return '承認';
    case 'rejected':
      return '却下';
    case 'reviewing':
      return '審査中にする';
    case 'pending':
      return '保留にする';
    case 'submitted':
      return '保留解除';
    default:
      return '保存';
  }
};

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const ConfirmModal = ({
  isOpen,
  formValues,
  originalValues,
  organizationCode,
  privateMoneyNames,
  shopTagSummaries,
  isSaveOnly = false,
  onClose,
  onConfirm,
}: Props) => {
  if (!formValues) return null;

  // Compare values to detect changes
  const isChanged = (key: keyof FormValues) => {
    if (!originalValues) return false;
    if (key === 'private_money_ids') {
      return !arraysEqual(formValues.private_money_ids, originalValues.private_money_ids);
    }
    return formValues[key] !== originalValues[key];
  };

  const isShopFieldChanged = (shopIndex: number, field: keyof ShopFormValues) => {
    if (!originalValues) return false;
    const original = originalValues.shops?.[shopIndex];
    if (!original) return false;
    return formValues.shops[shopIndex][field] !== original[field];
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <ModalHeader>
        {isSaveOnly
          ? 'この申請内容を保存します。よろしいですか？'
          : getHeaderMessage(formValues.status)}

        {!isSaveOnly && isIrreversibleStatus(formValues.status) && (
          <WarningMessage>
            <FA icon="exclamation-triangle" />
            <span>この操作は元に戻せません</span>
          </WarningMessage>
        )}
      </ModalHeader>

      <ModalContent>
        <ColumnSection>
          <ColumnHeader>基本情報</ColumnHeader>

          <Section>
            <SubSection>
              <SectionLabel>進捗状態</SectionLabel>
              <InfoText>{STATUS_LABELS[formValues.status]}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>組織コード</SectionLabel>
              <InfoText>{organizationCode || '自動採番'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                事業体の種類 {isChanged('business_type') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('business_type')}>
                {BUSINESS_TYPE_LABELS[formValues.business_type]}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                加盟するマネー {isChanged('private_money_ids') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('private_money_ids')}>
                {privateMoneyNames.join(', ') || '-'}
              </InfoText>
            </SubSection>

            {formValues.business_type === 'corporate' && (
              <SubSection>
                <SectionLabel>
                  法人番号 {isChanged('corporate_number') && <RevisedBadge />}
                </SectionLabel>
                <InfoText isBold={isChanged('corporate_number')}>
                  {formValues.corporate_number || '-'}
                </InfoText>
              </SubSection>
            )}

            <SubSection>
              <SectionLabel>
                {formValues.business_type === 'individual' ? '屋号（ない場合は氏名）' : '法人名'}{' '}
                {isChanged('name') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('name')}>{formValues.name || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                {formValues.business_type === 'individual'
                  ? '屋号（フリガナ）'
                  : '法人名（フリガナ）'}{' '}
                {isChanged('furigana') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('furigana')}>{formValues.furigana || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                代表者氏名 {isChanged('representative_name') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('representative_name')}>
                {formValues.representative_name || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                代表者氏名（フリガナ） {isChanged('representative_furigana') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('representative_furigana')}>
                {formValues.representative_furigana || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                代表者生年月日 {isChanged('representative_birth_date') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('representative_birth_date')}>
                {moment(formValues.representative_birth_date).format('YYYY-MM-DD') || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>郵便番号 {isChanged('postal_code') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('postal_code')}>{formValues.postal_code || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                {formValues.business_type === 'individual'
                  ? '納税地住所（開業届の記載住所）'
                  : '本店所在地（登記住所）'}{' '}
                {isChanged('address') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('address')}>{formValues.address || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>電話番号 {isChanged('tel') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('tel')}>{formValues.tel || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>メールアドレス {isChanged('email') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('email')}>{formValues.email || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>紹介元 {isChanged('referral_source') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('referral_source')}>
                {formValues.referral_source || '-'}
              </InfoText>
            </SubSection>

            {formValues.business_type === 'corporate' && (
              <>
                <SubSection>
                  <SectionLabel>
                    資本金（円） {isChanged('capital') && <RevisedBadge />}
                  </SectionLabel>
                  <InfoText isBold={isChanged('capital')}>
                    {formValues.capital ? Number(formValues.capital).toLocaleString() : '-'}
                  </InfoText>
                </SubSection>

                <SubSection>
                  <SectionLabel>
                    従業員数 {isChanged('number_of_employees') && <RevisedBadge />}
                  </SectionLabel>
                  <InfoText isBold={isChanged('number_of_employees')}>
                    {formValues.number_of_employees || '-'}
                  </InfoText>
                </SubSection>
              </>
            )}
          </Section>
        </ColumnSection>

        <Divider />

        <ColumnSection>
          <ColumnHeader>振り込み先情報</ColumnHeader>

          <Section>
            <SubSection>
              <SectionLabel>金融機関名 {isChanged('bank_name') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('bank_name')}>{formValues.bank_name || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                金融機関コード {isChanged('bank_code') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('bank_code')}>{formValues.bank_code || '-'}</InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>支店名 {isChanged('bank_branch_name') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('bank_branch_name')}>
                {formValues.bank_branch_name || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                支店コード {isChanged('bank_branch_code') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('bank_branch_code')}>
                {formValues.bank_branch_code || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                口座種別 {isChanged('bank_account_type') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('bank_account_type')}>
                {BANK_ACCOUNT_TYPE_LABELS[formValues.bank_account_type]}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>口座番号 {isChanged('bank_account') && <RevisedBadge />}</SectionLabel>
              <InfoText isBold={isChanged('bank_account')}>
                {formValues.bank_account || '-'}
              </InfoText>
            </SubSection>

            <SubSection>
              <SectionLabel>
                口座名義人 {isChanged('bank_account_holder_name') && <RevisedBadge />}
              </SectionLabel>
              <InfoText isBold={isChanged('bank_account_holder_name')}>
                {formValues.bank_account_holder_name || '-'}
              </InfoText>
            </SubSection>
          </Section>

          {formValues.shops && formValues.shops.length > 0 && (
            <>
              <ColumnHeader>店舗情報</ColumnHeader>
              <ShopsScrollArea>
                {formValues.shops.map((shop, i) => (
                  <div key={i}>
                    <SectionLabel>店舗情報{i + 1}</SectionLabel>
                    <Section>
                      <SubSection>
                        <SectionLabel>
                          店舗名 {isShopFieldChanged(i, 'shop_name') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'shop_name')}>
                          {shop.shop_name || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          店舗名（フリガナ）{' '}
                          {isShopFieldChanged(i, 'shop_name_furigana') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'shop_name_furigana')}>
                          {shop.shop_name_furigana || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          郵便番号 {isShopFieldChanged(i, 'postal_code') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'postal_code')}>
                          {shop.postal_code || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          住所 {isShopFieldChanged(i, 'address') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'address')}>
                          {shop.address || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          電話番号 {isShopFieldChanged(i, 'tel') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'tel')}>{shop.tel || '-'}</InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          メールアドレス {isShopFieldChanged(i, 'email') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'email')}>
                          {shop.email || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          担当者名 {isShopFieldChanged(i, 'contact_person_name') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'contact_person_name')}>
                          {shop.contact_person_name || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          担当者名（フリガナ）{' '}
                          {isShopFieldChanged(i, 'contact_person_name_furigana') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'contact_person_name_furigana')}>
                          {shop.contact_person_name_furigana || '-'}
                        </InfoText>
                      </SubSection>
                      <SubSection>
                        <SectionLabel>
                          店舗形態 {isShopFieldChanged(i, 'usage_type') && <RevisedBadge />}
                        </SectionLabel>
                        <InfoText isBold={isShopFieldChanged(i, 'usage_type')}>
                          {usageTypeLabel(shop.usage_type) || '-'}
                        </InfoText>
                      </SubSection>
                      {(shopTagSummaries?.[i] ?? []).map((entry) => (
                        <SubSection key={entry.groupId}>
                          <SectionLabel>
                            {entry.groupName} {entry.changed && <RevisedBadge />}
                          </SectionLabel>
                          <InfoText>{entry.itemNames.join('、') || '-'}</InfoText>
                        </SubSection>
                      ))}
                    </Section>
                  </div>
                ))}
              </ShopsScrollArea>
            </>
          )}
        </ColumnSection>
      </ModalContent>

      <ModalFooter>
        <Button variant="neutral" onClick={onClose}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={onConfirm}>
          {isSaveOnly ? '保存' : getConfirmButtonText(formValues.status)}
        </Button>
      </ModalFooter>
    </ReactModal>
  );
};

export default ConfirmModal;
